import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import './EmployeePaymentManagement.css';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY'
  }).format(amount);
};

const EmployeePaymentManagement = () => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [paymentOverview, setPaymentOverview] = useState([]);
  const [paymentStats, setPaymentStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  
  const [currentPayment, setCurrentPayment] = useState({
    employeeId: '',
    month: '',
    amount: 0,
    notes: ''
  });
  
  const [currentRate, setCurrentRate] = useState({
    employeeId: '',
    dailyRate: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Load data when component mounts or month changes
  useEffect(() => {
    loadPaymentData();
  }, [selectedMonth]);

  const loadPaymentData = async () => {
    setLoading(true);
    try {
      console.log('Loading payment data for month:', selectedMonth);
      
      const [overviewResponse, statsResponse] = await Promise.all([
        apiService.getMonthlyPaymentOverview(selectedMonth),
        apiService.getPaymentStats(selectedMonth)
      ]);
      
      console.log('Overview response:', overviewResponse.data);
      console.log('Stats response:', statsResponse.data);
      
      setPaymentOverview(overviewResponse.data.employees || []);
      setPaymentStats(statsResponse.data || {});
      setError(null);
    } catch (err) {
      console.error('Error loading payment data:', err);
      setError('Ödeme verileri yüklenirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle create new payment
  const handleAddPayment = (employee) => {
    const totalPaid = employee.totalPaid || 0;
    const remainingAmount = employee.calculatedPayment.totalAmount - totalPaid;
    
    setCurrentPayment({
      employeeId: employee.employee.id,
      month: selectedMonth,
      amount: remainingAmount > 0 ? remainingAmount : 0,
      notes: ''
    });
    setSelectedEmployee(employee);
    setShowPaymentModal(true);
  };

  // Handle set payment rate
  const handleSetRate = (employee) => {
    setCurrentRate({
      employeeId: employee.employee.id,
      dailyRate: employee.paymentRate.dailyRate || '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setSelectedEmployee(employee);
    setShowRateModal(true);
  };

  // Handle view payment history
  const handleViewPaymentHistory = async (employee) => {
    try {
      setLoading(true);
      const response = await apiService.getEmployeePaymentHistory(employee.employee.id, selectedMonth);
      console.log('Payment history response:', response.data);
      setPaymentHistory(response.data || []);
      setSelectedEmployee(employee);
      setShowPaymentHistoryModal(true);
    } catch (err) {
      console.error('Error loading payment history:', err);
      setError('Ödeme geçmişi yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle payment form input changes
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentPayment(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value
    }));
  };

  // Handle rate form input changes
  const handleRateInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit payment form
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (currentPayment.amount <= 0) {
      setError('Ödeme tutarı 0\'dan büyük olmalıdır');
      return;
    }
    
    const paymentData = {
      employeeId: currentPayment.employeeId,
      month: currentPayment.month,
      amount: currentPayment.amount,
      notes: currentPayment.notes
    };
    
    console.log('Submitting payment:', paymentData);
    
    try {
      const response = await apiService.createOrUpdatePayment(paymentData);
      console.log('Payment response:', response);
      
      setShowPaymentModal(false);
      resetPaymentForm();
      loadPaymentData();
    } catch (err) {
      console.error('Error saving payment:', err);
      setError('Ödeme kaydedilirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Submit rate form
  const handleRateSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentRate.dailyRate || parseFloat(currentRate.dailyRate) <= 0) {
      setError('Geçerli bir günlük ücret giriniz');
      return;
    }
    
    try {
      await apiService.setEmployeeDailyRate(currentRate.employeeId, {
        dailyRate: parseFloat(currentRate.dailyRate),
        effectiveFrom: currentRate.effectiveFrom,
        notes: currentRate.notes
      });
      setShowRateModal(false);
      resetRateForm();
      loadPaymentData();
    } catch (err) {
      console.error('Error setting rate:', err);
      setError('Günlük ücret ayarlanırken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Delete individual payment
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Bu ödeme kaydını silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await apiService.deleteEmployeePayment(paymentId);
      loadPaymentData();
      // If payment history modal is open, refresh it
      if (showPaymentHistoryModal && selectedEmployee) {
        handleViewPaymentHistory(selectedEmployee);
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError('Ödeme silinirken bir hata oluştu: ' + err.message);
    }
  };

  // Reset forms
  const resetPaymentForm = () => {
    setCurrentPayment({
      employeeId: '',
      month: '',
      amount: 0,
      notes: ''
    });
    setSelectedEmployee(null);
  };

  const resetRateForm = () => {
    setCurrentRate({
      employeeId: '',
      dailyRate: '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setSelectedEmployee(null);
  };

  // Close modals
  const closeAllModals = () => {
    setShowPaymentModal(false);
    setShowRateModal(false);
    setShowPaymentHistoryModal(false);
    resetPaymentForm();
    resetRateForm();
    setPaymentHistory([]);
    setError(null);
  };

  // Get month display name
  const getMonthDisplayName = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  if (loading && paymentOverview.length === 0) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="employee-payment-management">
      <div className="section-header">
        <h3>Personel Ödemeleri</h3>
        <div className="payment-controls">
          <div className="month-selector">
            <label htmlFor="month-select">Ay Seçin:</label>
            <input
              id="month-select"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button 
            className="error-close"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Payment Summary */}
      <div className="payment-summary">
        <h4>{getMonthDisplayName(selectedMonth)} Ayı Özeti</h4>
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-title">Toplam Personel</div>
            <div className="summary-value">{paymentOverview.length}</div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Toplam Borç</div>
            <div className="summary-value">
              {formatCurrency(paymentStats.totalDebt || 0)}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Ödenen Tutar</div>
            <div className="summary-value">
              {formatCurrency(paymentStats.totalPaid || 0)}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Kalan Borç</div>
            <div className="summary-value">
              {formatCurrency(paymentStats.remainingDebt || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Table */}
      <div className="payment-table-container">
        <div className="payment-table">
          <div className="table-header">
            <div>Personel</div>
            <div>Çalışılan Gün</div>
            <div>Günlük Ücret</div>
            <div>Toplam Hak Edilen</div>
            <div>Ödenen Tutar</div>
            <div>Kalan Borç</div>
            <div>İşlemler</div>
          </div>
          
          {paymentOverview.length === 0 ? (
            <div className="no-data">
              Bu ay için personel verisi bulunamadı.
            </div>
          ) : (
            paymentOverview.map(employeeData => {
              const { employee, workSummary, paymentRate, calculatedPayment } = employeeData;
              const totalPaid = employeeData.totalPaid || 0;
              const remainingDebt = calculatedPayment.totalAmount - totalPaid;
              const paymentCount = employeeData.paymentCount || 0;
              
              return (
                <div key={employee.id} className="table-row">
                  <div className="employee-info">
                    <div className="employee-name">
                      {employee.name} {employee.surname}
                    </div>
                    <div className="employee-position">{employee.position}</div>
                  </div>
                  <div className="work-info">
                   <div className="days-worked">{workSummary.daysWorked} gün</div>
                 </div>
                 
                 <div className="daily-rate">
                   {paymentRate.hasRate ? (
                     <div className="rate-display">
                       <div className="rate-amount">{formatCurrency(paymentRate.dailyRate)}</div>
                       <div className="rate-actions">
                         <button 
                           className="rate-edit-btn"
                           onClick={() => handleSetRate(employeeData)}
                           title="Ücreti Düzenle"
                         >
                           Düzenle
                         </button>
                       </div>
                     </div>
                   ) : (
                     <button 
                       className="set-rate-btn"
                       onClick={() => handleSetRate(employeeData)}
                     >
                       Ücret Belirle
                     </button>
                   )}
                 </div>
                 
                 <div className="calculated-amount">
                   {formatCurrency(calculatedPayment.totalAmount)}
                 </div>
                 
                 <div className="paid-amount">
                   <div className="amount-display">
                     {formatCurrency(totalPaid)}
                   </div>
                   {paymentCount > 0 && (
                     <div className="payment-count">
                       {paymentCount} ödeme
                     </div>
                   )}
                 </div>
                 
                 <div className={`remaining-debt ${remainingDebt > 0 ? 'has-debt' : 'no-debt'}`}>
                   <div className="debt-amount">
                     {formatCurrency(remainingDebt)}
                   </div>
                   {remainingDebt <= 0 && (
                     <div className="status-complete">✓ Tamamlandı</div>
                   )}
                 </div>
                 
                 <div className="action-buttons">
                   <div className="payment-actions">
                     {paymentRate.hasRate && (
                       <>
                         {remainingDebt > 0 && (
                           <button
                             className="add-payment-button"
                             onClick={() => handleAddPayment(employeeData)}
                             title="Ödeme Yap"
                           >
                             Ödeme Yap
                           </button>
                         )}
                         {paymentCount > 0 && (
                           <button
                             className="view-history-button"
                             onClick={() => handleViewPaymentHistory(employeeData)}
                             title="Ödeme Geçmişi"
                           >
                             Geçmiş ({paymentCount})
                           </button>
                         )}
                       </>
                     )}
                   </div>
                 </div>
               </div>
             );
           })
         )}
       </div>
     </div>

     {/* Payment Modal */}
     {showPaymentModal && (
       <div className="modal-overlay">
         <div className="modal">
           <div className="modal-header">
             <h3>Ödeme Yap</h3>
             <button
               className="close-button"
               onClick={closeAllModals}
             >
               &times;
             </button>
           </div>
           
           <form onSubmit={handlePaymentSubmit}>
             <div className="modal-content">
               <div className="form-group">
                 <label>Personel</label>
                 <input
                   type="text"
                   value={selectedEmployee ? `${selectedEmployee.employee.name} ${selectedEmployee.employee.surname}` : ''}
                   readOnly
                   className="readonly-input"
                 />
               </div>
               
               <div className="form-group">
                 <label>Ay</label>
                 <input
                   type="text"
                   value={getMonthDisplayName(selectedMonth)}
                   readOnly
                   className="readonly-input"
                 />
               </div>

               {selectedEmployee && (
                 <div className="payment-summary-info">
                   <div className="summary-row">
                     <span>Toplam Hak Edilen:</span>
                     <span className="amount">{formatCurrency(selectedEmployee.calculatedPayment.totalAmount)}</span>
                   </div>
                   <div className="summary-row">
                     <span>Daha Önce Ödenen:</span>
                     <span className="amount">{formatCurrency(selectedEmployee.totalPaid || 0)}</span>
                   </div>
                   <div className="summary-row total">
                     <span>Kalan Borç:</span>
                     <span className="amount">{formatCurrency((selectedEmployee.calculatedPayment.totalAmount - (selectedEmployee.totalPaid || 0)))}</span>
                   </div>
                 </div>
               )}
               
               <div className="form-group">
                 <label htmlFor="amount">Ödeme Tutarı (TL) *</label>
                 <input
                   type="number"
                   id="amount"
                   name="amount"
                   value={currentPayment.amount}
                   onChange={handlePaymentInputChange}
                   step="0.01"
                   min="0"
                   max={selectedEmployee ? (selectedEmployee.calculatedPayment.totalAmount - (selectedEmployee.totalPaid || 0)) : 0}
                   required
                 />
                 <small>En fazla kalan borç tutarı kadar ödeme yapabilirsiniz</small>
               </div>
               
               <div className="form-group">
                 <label htmlFor="notes">Notlar</label>
                 <textarea
                   id="notes"
                   name="notes"
                   value={currentPayment.notes}
                   onChange={handlePaymentInputChange}
                   rows="3"
                   placeholder="Ödeme ile ilgili notlar..."
                 ></textarea>
               </div>
             </div>
             
             <div className="form-actions">
               <button
                 type="button"
                 className="cancel-button"
                 onClick={closeAllModals}
               >
                 İptal
               </button>
               <button
                 type="submit"
                 className="save-button"
               >
                 Ödeme Yap
               </button>
             </div>
           </form>
         </div>
       </div>
     )}

     {/* Rate Modal */}
     {showRateModal && (
       <div className="modal-overlay">
         <div className="modal">
           <div className="modal-header">
             <h3>Günlük Ücret Belirle</h3>
             <button
               className="close-button"
               onClick={closeAllModals}
             >
               &times;
             </button>
           </div>
           
           <form onSubmit={handleRateSubmit}>
             <div className="modal-content">
               <div className="form-group">
                 <label>Personel</label>
                 <input
                   type="text"
                   value={selectedEmployee ? `${selectedEmployee.employee.name} ${selectedEmployee.employee.surname}` : ''}
                   readOnly
                   className="readonly-input"
                 />
               </div>
               
               <div className="form-row">
                 <div className="form-group">
                   <label htmlFor="dailyRate">Günlük Ücret (TL) *</label>
                   <input
                     type="number"
                     id="dailyRate"
                     name="dailyRate"
                     value={currentRate.dailyRate}
                     onChange={handleRateInputChange}
                     step="0.01"
                     min="0"
                     placeholder="0.00"
                     required
                   />
                 </div>
                 
                 <div className="form-group">
                   <label htmlFor="effectiveFrom">Geçerlilik Başlangıcı *</label>
                   <input
                     type="date"
                     id="effectiveFrom"
                     name="effectiveFrom"
                     value={currentRate.effectiveFrom}
                     onChange={handleRateInputChange}
                     required
                   />
                 </div>
               </div>
               
               <div className="form-group">
                 <label htmlFor="rateNotes">Notlar</label>
                 <textarea
                   id="rateNotes"
                   name="notes"
                   value={currentRate.notes}
                   onChange={handleRateInputChange}
                   rows="3"
                   placeholder="Ücret değişikliği hakkında notlar..."
                 ></textarea>
               </div>
               
               <div className="rate-info">
                 <p><strong>ℹ️ Bilgi:</strong> Yeni ücret belirtilen tarihten itibaren geçerli olacaktır. 
                 Önceki dönemler için hesaplanmış ödemeler etkilenmeyecektir.</p>
               </div>
             </div>
             
             <div className="form-actions">
               <button
                 type="button"
                 className="cancel-button"
                 onClick={closeAllModals}
               >
                 İptal
               </button>
               <button
                 type="submit"
                 className="save-button"
               >
                 Kaydet
               </button>
             </div>
           </form>
         </div>
       </div>
     )}

     {/* Payment History Modal */}
     {showPaymentHistoryModal && (
       <div className="modal-overlay">
         <div className="modal large-modal">
           <div className="modal-header">
             <h3>
               Ödeme Geçmişi - {selectedEmployee?.employee.name} {selectedEmployee?.employee.surname}
             </h3>
             <button
               className="close-button"
               onClick={closeAllModals}
             >
               &times;
             </button>
           </div>
           
           <div className="modal-content">
             {paymentHistory.length === 0 ? (
               <div className="no-data">Bu personel için bu ay ödeme geçmişi bulunamadı.</div>
             ) : (
               <div className="payment-history-table">
                 <div className="table-header">
                   <div>Ödeme Tarihi</div>
                   <div>Tutar</div>
                   <div>Notlar</div>
                   <div>İşlemler</div>
                 </div>
                 
                 {paymentHistory.map(payment => (
                   <div key={payment.id} className="table-row">
                     <div className="payment-date">
                       {formatDateTime(payment.createdAt)}
                     </div>
                     <div className="payment-amount">
                       {formatCurrency(payment.totalAmount || payment.amount)}
                     </div>
                     <div className="payment-notes">
                       {payment.notes || '-'}
                     </div>
                     <div className="payment-actions">
                       <button
                         className="delete-button"
                         onClick={() => handleDeletePayment(payment.id)}
                         title="Ödemeyi Sil"
                       >
                         Sil
                       </button>
                     </div>
                   </div>
                 ))}
                 
                 <div className="history-summary">
                   <div className="summary-row">
                     <span>Toplam Ödeme Sayısı:</span>
                     <span>{paymentHistory.length}</span>
                   </div>
                   <div className="summary-row total">
                     <span>Toplam Ödenen Tutar:</span>
                     <span>{formatCurrency(paymentHistory.reduce((sum, p) => sum + parseFloat(p.totalAmount || p.amount || 0), 0))}</span>
                   </div>
                 </div>
               </div>
             )}
           </div>
           
           <div className="form-actions">
             <button
               type="button"
               className="cancel-button"
               onClick={closeAllModals}
             >
               Kapat
             </button>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

export default EmployeePaymentManagement;